import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Pencil } from "lucide-react";
import {
  getDateTimeFormate,
  hideInfo,
  hideMobile,
  getDob,
  hideData,
  hideEmail,
} from "../../sharedfiles/helper";
import { CircleCheck, X } from "lucide-react";
import { COMMON_CONSTANTS, PRIMARYPLAYER_CONSTANTS } from "../../sharedfiles/Constants"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Switch } from "../../ui/switch";
import { Button } from "../../ui/button";
import { apiCaller } from "../../api/apiCaller";
import { playerapi } from "../../sharedfiles/EndpointConfig";
import { toast } from "react-toastify";
import { getPlayerGeneralDetail } from "./GeneralDetailSlice";
import { triggerRiskSummaryRefresh } from "../AppSlice";
import Spinner from "../Common/Spinner/Spinner";

export const DetailsWrapper = (props) => {
  const { title, children, small } = props;

  return (
    <div className={`flex items-center justify-between py-3 border-b border-border/50 last:border-0 ${small ? 'gap-4' : ''}`}>
      <div className='text-sm font-medium text-muted-foreground shrink-0 whitespace-nowrap'>
        {title}
      </div>
      <div
        className='font-medium text-sm text-foreground flex-1 text-right min-w-0 truncate'
        title={typeof children === 'string' || typeof children === 'number' ? String(children) : undefined}
      >
        {children}
      </div>
    </div>
  )
}

const GeneralDetails = (props) => {
  const [masking, setMasking] = useState(false);
  const [isStakeFactorActive, setStakeFactorActive] = useState(false);
  const [isVIPStatusActive, setVIPStatusActive] = useState(false);
  const [vipStatusValue, setVIPStatusValue] = useState("0");
  const [stakeFactorValue, setStakeFactorValue] = useState("");
  const [autoUpdate, setAutoUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [commPref, setCommPref] = useState({
    promotionalEmail: false,
    pushNotificationPhone: false,
    sms: false,
    phoneCalls: false,
  });
  const [vipLevels, setVipLevels] = useState([]);
  const [profilingTags, setProfilingTags] = useState({ systemTags: [], customTags: [] });
  const [profilingTagsLoading, setProfilingTagsLoading] = useState(true);

  const dispatch = useDispatch();

  useEffect(() => {
    const userId = props.playerData?.userId;
    if (!userId) return;
    setProfilingTagsLoading(true);
    apiCaller(playerapi.getUserAllTags(userId), "GET")
      .then((res) => {
        if (res?.status === 200 && res?.data) {
          setProfilingTags({
            systemTags: res.data.systemTags || [],
            customTags: res.data.customTags || [],
          });
        } else {
          setProfilingTags({ systemTags: [], customTags: [] });
        }
      })
      .catch(() => setProfilingTags({ systemTags: [], customTags: [] }))
      .finally(() => setProfilingTagsLoading(false));
  }, [props.playerData?.userId]);

  useEffect(() => {
    if (isStakeFactorActive && props.playerData?.userOtherInfo?.stakeFactor !== undefined) {
      setStakeFactorValue(props.playerData.userOtherInfo.stakeFactor);
      setAutoUpdate(props.playerData.userOtherInfo?.stakeFactorAutoUpdate)
    }
  }, [isStakeFactorActive, props.playerData, setAutoUpdate]);


  const handleStakeFactorUpdate = async () => {

    if (stakeFactorValue === undefined || stakeFactorValue === null || stakeFactorValue === '') {
      toast.error("Stake factor is required.");
      return;
    }

    const stake = parseFloat(stakeFactorValue);
    if (stake > 1) {
      toast.error("Stake factor must not be greater than 1.0");
      return;
    }

    if (stake <= 0) {
      toast.error("Stake factor must be at least 0.1");
      return;
    }

    if (stakeFactorValue !== undefined && stakeFactorValue !== null && stakeFactorValue !== '') {
      setLoading(true);
      const postData = {
        stakeFactor: stakeFactorValue,
        stakeFactorAutoUpdate: autoUpdate,
        userId: props.playerData?.userId,
      }
      const response = await apiCaller(playerapi.updateStakeFactor, "POST", postData);
      if (response.status === 200) {
        setLoading(false);
        toast.success("Stake Factor updated successfully.");
        dispatch(getPlayerGeneralDetail({ user_id: props.playerData?.userId }));
        dispatch(triggerRiskSummaryRefresh());
        setStakeFactorActive(false);
      } else {
        setLoading(false);
        toast.error("Stake Factor updation failed.");
      }
    }
  };
  
  useEffect(() => {
    if (isVIPStatusActive && props.playerData?.userId) {
      apiCaller(playerapi.getVIPLevels, "GET")
        .then((res) => {
          if (res?.data) setVipLevels(res.data);
        })
        .catch(() => {});
    }
  }, [isVIPStatusActive, props.playerData?.userId]);

  const handleVIPStatusUpdate = async () => {
    if (!vipStatusValue || vipStatusValue === "0") {
      toast.error("Please select a VIP level.");
      return;
    }
    try {
      setLoading(true);
      await apiCaller(
        playerapi.assignVIPTag(vipStatusValue, props.playerData?.userId),
        "POST",
        {}
      );
      toast.success("VIP level updated successfully.");
      dispatch(getPlayerGeneralDetail({ user_id: props.playerData?.userId }));
      setVIPStatusActive(false);
    } catch {
      toast.error("VIP level update failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props.userCommonPrefrence) {
      setCommPref({
        promotionalEmail: props.userCommonPrefrence.promotionalEmail ?? false,
        pushNotificationPhone: props.userCommonPrefrence.pushNotificationPhone ?? false,
        sms: props.userCommonPrefrence.sms ?? false,
        phoneCalls: props.userCommonPrefrence.phoneCalls ?? false,
      });
    }
  }, [props.userCommonPrefrence]);

  const handleCommPrefChange = async (field, newValue) => {
    const prev = commPref[field];
    setCommPref((s) => ({ ...s, [field]: newValue }));

    const apiField = field === 'pushNotificationPhone' ? 'pushNotificationphone' : field;
    const postData = {
      dob: props.playerData?.dob,
      firstName: props.playerData?.firstName,
      lastName: props.playerData?.lastName,
      email: props.playerData?.userOtherInfo?.email,
      phoneCalls: commPref.phoneCalls,
      promotionalEmail: commPref.promotionalEmail,
      pushNotificationphone: commPref.pushNotificationPhone,
      sms: commPref.sms,
      keepLevelSticky: props.playerData?.userOtherInfo?.keepLevelSticky,
      vip: props.playerData?.vip,
      userType: props.playerData?.userType,
      affliateBtag: props.playerData?.userOtherInfo?.affliateBtag,
      [apiField]: newValue,
    };
    try {
      const url = `${playerapi.getUpdatePlayer}${props.playerData?.userId}`;
      await apiCaller(url, "PUT", postData);
      toast.success("Updated successfully.");
    } catch {
      setCommPref((s) => ({ ...s, [field]: prev }));
      toast.error("Update failed.");
    }
  };

  useEffect(() => {
    let mounting = true;
    if (mounting) {
      if (localStorage.getItem("isMasking") === "DATA_MASKING") {
        setMasking(true)
      } else {
        setMasking(false)
      }
    }

    return () => mounting = false;
  }, []);

  const handleCloseModal = () => {
    setStakeFactorActive(false);
    setVIPStatusActive(false);
  };

  return (
    <>
      <div className='space-y-6' id='CMS-generalDetails'>
        <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6'>
          {/* Account Information Card */}
          <div className='bg-card rounded-lg border border-border p-5 shadow-sm'>
            <h3 className='text-base font-semibold mb-4 pb-3 border-b border-border'>Account Information</h3>
            <div className='space-y-0'>
              <DetailsWrapper title={"Customer ID"}>
                {props.playerData.userId}
              </DetailsWrapper>
              <DetailsWrapper title={"Date of Birth"}>
                {masking ? null : (
                  props.playerData.userLoginDetails != undefined &&
                  hideInfo(getDob(props.playerData.dob), 1, true)
                )}
              </DetailsWrapper>
              <DetailsWrapper title={"User Name"} small>
                {masking && props.playerData.userLoginDetails != undefined &&
                  Number(props.playerData.nickName) ? hideMobile(props.playerData.nickName) : props.playerData.nickName}
              </DetailsWrapper>
              <DetailsWrapper title={"Surname"}>
                {props.playerData.surName}
              </DetailsWrapper>
              <DetailsWrapper title={"Gender"}>
                {props.playerData?.gender}
              </DetailsWrapper>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className='bg-card rounded-lg border border-border p-5 shadow-sm'>
            <h3 className='text-base font-semibold mb-4 pb-3 border-b border-border'>Contact Information</h3>
            <div className='space-y-0'>
              <DetailsWrapper title={"Phone Number"}>
                {props.playerData.userOtherInfo != undefined && props.playerData.userOtherInfo.phone != null && (
                  <div className='flex items-center justify-end gap-0.5 min-w-0'>
                    <span
                      className='truncate min-w-0'
                      title={props.playerData.userOtherInfo.countryCode + props.playerData.userOtherInfo.phone}
                    >
                      {masking ? hideMobile(props.playerData.userOtherInfo.countryCode + props.playerData.userOtherInfo.phone, false) :
                        props.playerData.userOtherInfo.countryCode + props.playerData.userOtherInfo.phone}
                    </span>
                    {props.playerData.userOtherInfo.phone != null &&
                      props.playerData.userOtherInfo.mobileVerified ? (
                      <CircleCheck className="h-4 w-4 text-green-600 shrink-0" />
                    ) : (
                      props.playerData.userOtherInfo.phone != null && (
                        <X className="h-4 w-4 text-red-600 shrink-0" />
                      )
                    )}
                  </div>
                )}
              </DetailsWrapper>
              <DetailsWrapper title={"Email"} small>
                {props.playerData.userOtherInfo != undefined && (
                  <div className='flex items-center justify-end gap-0.5 min-w-0'>
                    <span className='truncate min-w-0' title={props.playerData.userOtherInfo.email}>
                      {masking ? hideEmail(props.playerData.userOtherInfo.email, false) : props.playerData.userOtherInfo.email}
                    </span>
                    {props.playerData.userOtherInfo.email != null &&
                      props.playerData.userOtherInfo.emailVerified ? (
                      <CircleCheck className="h-4 w-4 text-green-600 shrink-0" />
                    ) : (
                      props.playerData.userOtherInfo.email != null && (
                        <X className="h-4 w-4 text-red-600 shrink-0" />
                      )
                    )}
                  </div>
                )}
              </DetailsWrapper>
              <DetailsWrapper title={"Address"}>
                {props.playerData.userOtherInfo?.address}
              </DetailsWrapper>
              <DetailsWrapper title={"State"}>
                {props.playerData.userOtherInfo?.state}
              </DetailsWrapper>
              <DetailsWrapper title={"Country"}>
                {props.financialData.playerSummaryVw != undefined && (
                  masking ? hideData(props?.financialData?.playerSummaryVw?.country, false) : props?.financialData?.playerSummaryVw?.country
                )}
              </DetailsWrapper>
            </div>
          </div>

          {/* Login & Activity Card */}
          <div className='bg-card rounded-lg border border-border p-5 shadow-sm'>
            <h3 className='text-base font-semibold mb-4 pb-3 border-b border-border'>Login & Activity</h3>
            <div className='space-y-0'>
              <DetailsWrapper title={"Registered Date"}>
                {props.financialData.playerSummaryVw &&
                  getDateTimeFormate(props.financialData.playerSummaryVw.registeredDate)}
              </DetailsWrapper>
              <DetailsWrapper title={"Registration IP"}>
                {props.playerData.userLoginDetails != undefined && (
                  masking ? hideData(props.playerData.userLoginDetails.signupIp, false) : props.playerData.userLoginDetails.signupIp
                )}
              </DetailsWrapper>
              <DetailsWrapper title={"Last Visit"}>
                {props.playerData.userLoginDetails?.lastLoginTime != undefined &&
                  getDateTimeFormate(props.playerData.userLoginDetails.lastLoginTime)}
              </DetailsWrapper>
              <DetailsWrapper title={"Last Visit IP"}>
                {props.playerData.userLoginDetails != undefined && (
                  masking ? hideData(props.playerData.userLoginDetails.lastLoginIp, false) : props.playerData.userLoginDetails.lastLoginIp
                )}
              </DetailsWrapper>
              <DetailsWrapper title={"Logout Time"}>
                {props.playerData.userLoginDetails?.lastLogout != undefined &&
                  getDateTimeFormate(props.playerData.userLoginDetails.lastLogout)}
              </DetailsWrapper>
              <DetailsWrapper title={"Login Device"}>
                {props.financialData.betDetails?.primaryChannel?.replace(/^\w/, (c) => c.toUpperCase())}
              </DetailsWrapper>
            </div>
          </div>

          {/* Affiliate & Marketing Card */}
          <div className='bg-card rounded-lg border border-border p-5 shadow-sm'>
            <h3 className='text-base font-semibold mb-4 pb-3 border-b border-border'>Affiliate & Marketing</h3>
            <div className='space-y-0'>
              <DetailsWrapper title={"Affiliate Site ID / Name"}>
                {props.playerData?.userOtherInfo?.affliateSiteName}
              </DetailsWrapper>
              <DetailsWrapper title={"Affiliate Btag"}>
                {props.playerData?.userOtherInfo?.affliateBtag}
              </DetailsWrapper>
              <DetailsWrapper title={"Referred By"}>
                {props.playerData.userLoginDetails?.referralCode}
              </DetailsWrapper>
              <DetailsWrapper title={"Referral Code"}>
                {props.playerData.userRefCode}
              </DetailsWrapper>
              <DetailsWrapper title={"Campaign Id"}>
                {props.playerData?.userOtherInfo?.campaignId || "-"}
              </DetailsWrapper>
              <DetailsWrapper title={"Channel Id"}>
                {props.playerData?.userOtherInfo?.channelId || "-"}
              </DetailsWrapper>
            </div>
          </div>

          {/* Player Settings Card */}
          <div className='bg-card rounded-lg border border-border p-5 shadow-sm'>
            <h3 className='text-base font-semibold mb-4 pb-3 border-b border-border'>Player Settings</h3>
            <div className='space-y-0'>
              <DetailsWrapper title={"User Type"}>
                {props.playerData?.userType}
              </DetailsWrapper>
              {/* <DetailsWrapper title={"VIP Status"}>
                <input
                  id='vip'
                  type='checkbox'
                  checked={props.playerData?.vip}
                  disabled
                  className='pointer-events-none h-4 w-4'
                />
              </DetailsWrapper> */}
              <DetailsWrapper title={"VIP Status"}>
                {props.playerData.userOtherInfo != undefined && (
                  <div className='flex items-center justify-end gap-2'>
                    <Pencil className="h-4 w-4 cursor-pointer hover:scale-110 transition-transform text-blue-500" onClick={() => { setVIPStatusActive(true) }} />
                    <Dialog open={isVIPStatusActive} onOpenChange={handleCloseModal}>
                      <DialogContent className="max-w-2xl max-h-screen overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Edit VIP Status</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-5">
                          <div className="space-y-2">
                            <Label htmlFor="vipLevel">VIP levels</Label>
                            <Select
                              value={vipStatusValue}
                              onValueChange={(value) => setVIPStatusValue(value)}
                            >
                              <SelectTrigger id="vipLevel">
                                <SelectValue placeholder="Select VIP Level" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">Select VIP Level</SelectItem>
                                {vipLevels.map((level) => (
                                  <SelectItem key={level.userTagId} value={String(level.id)}>
                                    {level.tagName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-4 pt-4 border-t border-border">
                            
                            <div className="flex justify-end gap-3">
                              <Button
                                type="button"
                                onClick={() => handleCloseModal(false)}
                                variant="outline"
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                onClick={handleVIPStatusUpdate}
                                disabled={loading}
                                className="min-w-[110px]"
                              >
                                {loading ? <Spinner size="sm" /> : "Update"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </DetailsWrapper>
              <DetailsWrapper title={"Keep Level Sticky"}>
                <input
                  id='KeepLevelSticky'
                  type='checkbox'
                  checked={props.playerData?.userOtherInfo?.keepLevelSticky}
                  disabled
                  className='pointer-events-none h-4 w-4'
                />
              </DetailsWrapper>
              <DetailsWrapper title={"Terms & Conditions"}>
                {props.userCommonPrefrence?.termsAndConditions ? "Verified" : "Not Verified"}
              </DetailsWrapper>
              <DetailsWrapper title={"Stake Factor"}>
                {props.playerData.userOtherInfo != undefined && (
                  <div className='flex items-center justify-end gap-2'>
                    <span>
                      {props.playerData?.userOtherInfo?.stakeFactor || "-"}
                    </span>
                    <Pencil className="h-4 w-4 cursor-pointer hover:scale-110 transition-transform text-blue-500" onClick={() => { setStakeFactorActive(true) }} />
                    <Dialog open={isStakeFactorActive} onOpenChange={handleCloseModal}>
                      <DialogContent className="max-w-2xl max-h-screen overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Edit Stake Factor</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-5">
                          <div className="space-y-2">
                            <Label htmlFor="stakeFactor">Stake Factor</Label>
                            <Input
                              id="stakeFactor"
                              placeholder="Stake Factor"
                              value={stakeFactorValue}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*\.?\d*$/.test(value)) {
                                  setStakeFactorValue(value);
                                }
                              }}
                              type="text"
                              disabled={autoUpdate === false}
                            />
                          </div>
                          <div className="space-y-4 pt-4 border-t border-border">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-2">
                                <Label
                                  htmlFor="stakeFactorToggle"
                                  className="text-sm font-medium"
                                >
                                  Auto Updates
                                </Label>
                                <Switch
                                  id="stakeFactorToggle"
                                  checked={autoUpdate}
                                  onCheckedChange={(checked) => setAutoUpdate(checked)}
                                />
                              </div>
                            </div>
                            <div className="flex justify-end gap-3">
                              <Button
                                type="button"
                                onClick={() => handleCloseModal(false)}
                                variant="outline"
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                onClick={handleStakeFactorUpdate}
                                disabled={loading}
                                className="min-w-[110px]"
                              >
                                {loading ? <Spinner size="sm" /> : "Update"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </DetailsWrapper>
            </div>
          </div>

          {/* Player Profiling Card */}
          <div className='bg-card rounded-lg border border-border p-5 shadow-sm'>
            <h3 className='text-base font-semibold mb-4 pb-3 border-b border-border'>Player Profiling</h3>
            <div className='space-y-4'>
              <div>
                <p className='text-sm font-medium text-muted-foreground mb-2'>System Tags</p>
                <div className='flex flex-wrap gap-2'>
                  {profilingTagsLoading ? (
                    <Spinner size="sm" />
                  ) : profilingTags.systemTags.length > 0 ? (
                    profilingTags.systemTags.map((tag) => (
                      <span
                        key={tag}
                        className='text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className='text-sm text-foreground'>-</span>
                  )}
                </div>
              </div>
              <div>
                <p className='text-sm font-medium text-muted-foreground mb-2'>Custom Tags</p>
                <div className='flex flex-wrap gap-2'>
                  {profilingTagsLoading ? (
                    <Spinner size="sm" />
                  ) : profilingTags.customTags.length > 0 ? (
                    profilingTags.customTags.map((tag) => (
                      <span
                        key={tag}
                        className='text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className='text-sm text-foreground'>-</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Communication Preferences Card */}
          <div className='bg-card rounded-lg border border-border p-5 shadow-sm lg:col-span-2 xl:col-span-3'>
            <h3 className='text-base font-semibold mb-4 pb-3 border-b border-border'>{PRIMARYPLAYER_CONSTANTS.header_communicationPreference}</h3>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
              <div className='flex items-center gap-2 p-3 rounded-md bg-muted/30'>
                <input
                  id='PE'
                  type='checkbox'
                  checked={commPref.promotionalEmail}
                  onChange={(e) => handleCommPrefChange('promotionalEmail', e.target.checked)}
                  className='h-4 w-4 rounded border-gray-300 cursor-pointer'
                />
                <label htmlFor='PE' className='text-sm font-medium cursor-pointer'>
                  {COMMON_CONSTANTS.header_promoEmail}
                </label>
              </div>
              <div className='flex items-center gap-2 p-3 rounded-md bg-muted/30'>
                <input
                  id='PN'
                  type='checkbox'
                  checked={commPref.pushNotificationPhone}
                  onChange={(e) => handleCommPrefChange('pushNotificationPhone', e.target.checked)}
                  className='h-4 w-4 rounded border-gray-300 cursor-pointer'
                />
                <label htmlFor='PN' className='text-sm font-medium cursor-pointer'>
                  {COMMON_CONSTANTS.header_notification}
                </label>
              </div>
              <div className='flex items-center gap-2 p-3 rounded-md bg-muted/30'>
                <input
                  id='SMS'
                  type='checkbox'
                  checked={commPref.sms}
                  onChange={(e) => handleCommPrefChange('sms', e.target.checked)}
                  className='h-4 w-4 rounded border-gray-300 cursor-pointer'
                />
                <label htmlFor='SMS' className='text-sm font-medium cursor-pointer'>
                  {COMMON_CONSTANTS.header_sms}
                </label>
              </div>
              <div className='flex items-center gap-2 p-3 rounded-md bg-muted/30'>
                <input
                  id='PC'
                  type='checkbox'
                  checked={commPref.phoneCalls}
                  onChange={(e) => handleCommPrefChange('phoneCalls', e.target.checked)}
                  className='h-4 w-4 rounded border-gray-300 cursor-pointer'
                />
                <label htmlFor='PC' className='text-sm font-medium cursor-pointer'>{COMMON_CONSTANTS.header_phnCalls}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
function mapStateToProps(state) {
  return {
    playerData: state.generaldetail?.playerData,
    financialData: state.primaryplayer.financialData,
    addnoteid: state.addNotesSlice.addnoteid,
    userCommonPrefrence: state.generaldetail.userCommonPrefrence,
  };
}
export default connect(mapStateToProps)(GeneralDetails);